<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME } from '@modules/affiliates-approved/constants';
import VAffiliateTrackersSettingsTabs from '@modules/affiliates-approved/submodules/trackers-settings/components/VAffiliateTrackersSettingsTabs/VAffiliateTrackersSettingsTabs.vue';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS } from '@modules/affiliates-approved/submodules/trackers-settings/constants';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { assert, isNumber, isString } from '@utils/guards';

import type { IAffiliateMainTrackersSettingsForm } from '../../types';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_MAIN } from '../../constants';
import { useAffiliateSaveTrackersSettingsMutation } from '../../mutations';
import { useGetAffiliateTrackersSettingsQuery } from '../../queries';

const route = useRoute();
const { getTranslation } = useI18n();

const affiliateId = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));

const { data, isLoading } = useGetAffiliateTrackersSettingsQuery(affiliateId);

const {
  mutateAsync,
  isLoading: isSaveLoading,
  errors,
  clearError,
} = useAffiliateSaveTrackersSettingsMutation();

const formSchema = computed<FormSchema<IAffiliateMainTrackersSettingsForm>>(() => ({
  max_count_trackers: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_MAIN, 'MAIN_FORM_MAX_COUNT_TRACKERS').value,
    value: data.value?.affiliate.max_count_trackers,
    required: true,
    maxFractionDigits: 0,
  },
  current_count_tracker: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_MAIN, 'MAIN_FORM_CURRENT_COUNT_TRACKER').value,
    value: data.value?.affiliate.trackers_count,
    maxFractionDigits: 0,
    readonly: true,
  },
}));

function onSubmit({ max_count_trackers }: IAffiliateMainTrackersSettingsForm): void {
  assert(isNumber(affiliateId.value));

  void mutateAsync({
    id: affiliateId.value,
    affiliate: {
      max_count_trackers,
      condition_id: data.value?.affiliate.condition_id,
    },
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'TrackersSettingsMainPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_HEADER')"
  >
    <VAffiliateTrackersSettingsTabs />
    <VLoader
      :loading="isLoading"
      min-height="200px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_SAVE')"
        :is-loading="isSaveLoading"
        :custom-errors="errors"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_BACK')"
            skip-history
            :to="{
              name: ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME,
            }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
